import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';

import { CopyValue } from 'components/CopyValue';
import { DetailExternalLink, DetailFormDatePicker, DetailFormTextArea, DetailValue } from 'components/DetailsView';
import { SectionContainer } from 'components/DetailsView/Section';

import { dateFromString, formatDateAsAnniversary, getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { SupplyOrderState } from 'data/__generated__';

import { SupplyOrderDetails, SupplyOrderFormValues } from '../supplyOrderFormValues';

/**
 * Les dates de l'achat, ses identifiants et son bon de commande. Modifiable, contrairement aux tuiles clés.
 *
 * Une colonne par groupe. En ligne, les champs d'un groupe s'étalaient sur toute la largeur de l'écran
 * loin de leur libellé, et les sélecteurs de date — qui gardent la largeur de leur input — laissaient
 * une demi-page de vide à droite.
 */
export const OrderInfoSection = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { control, register } = useFormContext<SupplyOrderFormValues>();

    const isInRack = supplyOrder.state === SupplyOrderState.InRack;
    // Une fois l'achat parti chez le fournisseur, sa date de commande n'est plus la nôtre à changer.
    const canEditOrderDate = [SupplyOrderState.Created, SupplyOrderState.Ready].includes(supplyOrder.state);

    return (
        <SectionContainer title="Dates et références" isInitiallyOpen>
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2 xl:grid-cols-[repeat(2,minmax(0,280px))_minmax(0,1fr)]">
                    <FieldColumn label="Dates">
                        <DetailFormDatePicker
                            label="Commandé le"
                            placeholder="Date de commande"
                            name="orderDate"
                            defaultValue={supplyOrder.orderDate}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                            isClearable={canEditOrderDate}
                            isDisabled={!canEditOrderDate}
                        />
                        <DetailFormDatePicker
                            label="Date de livraison prévue"
                            placeholder="Date de livraison prévue"
                            name="dateDeliveryScheduled"
                            defaultValue={supplyOrder.dateDeliveryScheduled}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                            isClearable={!isInRack}
                            isDisabled={isInRack}
                            error={
                                supplyOrder.scheduledDeliveryDateValidity
                                    ? undefined
                                    : 'Incompatible avec la date de commande'
                            }
                        />
                        <DetailFormDatePicker
                            label="Date de la prochaine commande"
                            sublabel="Si définie, les prédictions sont calculées sur cette date et l'achat passe dans les achats manuels."
                            placeholder="Date de la prochaine commande"
                            name="nextOrderDate"
                            defaultValue={supplyOrder.nextOrderDate}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                            isClearable={!isInRack}
                            isDisabled={isInRack}
                        />
                        <DetailFormDatePicker
                            label="Début des menus hebdomadaires"
                            placeholder="Début des menus hebdomadaires"
                            name="weeklyMenusStartDate"
                            defaultValue={supplyOrder.weeklyMenusStartDate}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                            isClearable
                        />
                    </FieldColumn>

                    <FieldColumn label="Références">
                        <CopyValue label="ID" value={supplyOrder._id} />
                        <DetailValue label="Numéro de commande" value={supplyOrder.number} />
                        <DetailExternalLink
                            label="Bon de commande (Google Sheet)"
                            href={
                                supplyOrder.spreadsheetId
                                    ? `https://docs.google.com/spreadsheets/d/${supplyOrder.spreadsheetId}`
                                    : null
                            }
                            name={supplyOrder.spreadsheetName}
                            emptyLabel="Pas encore généré"
                            dataTest="supply-order-spreadsheet"
                        />
                    </FieldColumn>

                    <FieldColumn label="Commentaires" className="md:col-span-2 xl:col-span-1">
                        <DetailFormTextArea
                            label="Usage interne"
                            className="min-h-32"
                            defaultValue={supplyOrder.comment ?? ''}
                            {...register('comment')}
                        />
                    </FieldColumn>
                </div>

                {/* Traçabilité : ni des dates métier, ni des champs de saisie. */}
                <div className="border-t border-border pt-3 text-xs text-muted-foreground">
                    Créé le {getExtendedFormattedDatetime(supplyOrder.createdAt)} · Dernière mise à jour le{' '}
                    {getExtendedFormattedDatetime(supplyOrder.updatedAt)}
                </div>
            </div>
        </SectionContainer>
    );
};

const FieldColumn = ({ label, className, children }: { label: string; className?: string; children: ReactNode }) => (
    <div className={cn('flex flex-col gap-2', className)}>
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className="flex flex-col gap-4">{children}</div>
    </div>
);
